// M-95 (promo): トーク画面で 1 問だけ答えられる簡易アンケート（クイックリプライ + postback）の
// **選択肢・postback data・返信文言の真実源**（LINE SDK 非依存の純関数）。
//
// なぜ shared に置くか（M-89 PR-B / `promoAutomationTemplates.ts` と同じ理由）:
//   管理画面（tenant-promo の配信作成）が「実際に送られる見た目」をプレビューする必要があり、
//   LINE SDK 依存の `functions/src/promo/` に持つとブラウザから読めず**書き写し → drift** になる。
//   ここ 1 箇所を functions（送信・受信）と tenant-promo（プレビュー）の双方が呼ぶ。
//
// LIFF 版（`apps/customer` の SurveyPage / `promoSurveyAnswer` Callable）との関係:
//   **回答は同じ扱い**にする — `promo_survey_responses` に同じ形で保存し、興味タグ・確度エンジン・
//   配信の絞り込みがそのまま効く（M-95 の確定事項）。車検満了年月の追加設問
//   （M-79 / `askInspectionDue`）は**年・月の 2 段階選択が要るため扱えない**。
//   送信側 UI がその旨を明示する責務を負う（黙って無視すると「設定したのに集まらない」になる）。
//
// M-106: 選択肢を**任意の選択肢 id へ汎用化**した（旧: 確度 4 択 `SURVEY_CHOICE_KEYS` 固定）。
//   既定 4 択のキー（`not_interested` 等）は**それ自体が選択肢 id**なので、`c=` に任意の id を
//   載せるのは既存 postback 形式の**上位互換**（形式もキーも変えていない）。変えたのは
//   「`c` を閉集合で検証する」のをやめて**形式のみ**にし、実在確認を受信側の定義照合
//   （`pickAnswersDefinedInSurvey`）へ寄せた点だけ。閉集合検証を 2 箇所に持つと、
//   アンケート定義を編集しただけで postback が落ちる（= 無言で回答が取れない）。
import { DEFAULT_SURVEY_CHOICE_LABELS, isValidSurveyOptionId, } from './promoTypes.js';
/**
 * postback `data` の種別タグ（`t=` の値）。
 *
 * **v1 は形式の世代**。既存の M1-01（`m1_01_3d__action=...`）は id を含まない固定文字列で
 * 済んでいたが、こちらは `surveyId`（最大 128 文字・任意文字）を載せるため、区切り文字が
 * id に混入しうる `__` 連結ではなく **query 形式（`URLSearchParams`）** にしている。
 */
export const SURVEY_POSTBACK_TYPE = 'psv1';
/**
 * M-111: **「回答を修正する」** の postback 種別（`t=` の値）。
 *
 * 回答そのもの（`psv1`）と**別のタグにする**理由: `psv1` の payload は `choice` が必須で、
 * 修正要求には選ばれた選択肢が無い。番兵値（`c=__edit__` 等）で兼用すると、
 * **選択肢 id として正当な文字列と衝突しうる**（`isValidSurveyOptionId` は任意文字を通す）。
 * タグを分ければ、受信側は `parse` を 2 本試すだけで衝突なく判別できる。
 */
export const SURVEY_EDIT_POSTBACK_TYPE = 'psve1';
/** LINE の postback `data` 上限（バイトではなく文字数 / Messaging API 仕様）。 */
export const LINE_POSTBACK_DATA_MAX = 300;
/** LINE のクイックリプライ 1 項目のラベル上限。 */
export const LINE_QUICK_REPLY_LABEL_MAX = 20;
/**
 * LINE のクイックリプライの項目数上限（Messaging API 仕様）。
 *
 * M-106 で選択肢を任意化したため、これが**実際に効く上限**になった（4 択固定だった間は
 * 到達不能で、宣言だけがあり参照ゼロだった）。超えた分を黙って切り落とすと
 * **管理画面で設定した選択肢の一部が顧客に出ない**（しかも切られた選択肢を選んだ人が
 * ゼロ件になるだけなので、集計を見ても異常だと分からない）ため、
 * `areSurveyQuickReplyItemsWithinCount` で**送信ごと止める**（fail-closed）。
 */
export const LINE_QUICK_REPLY_MAX_ITEMS = 13;
/** `campaignId` として受理する形式（`promoSendSegmentPush` の zod と同じ）。 */
const CAMPAIGN_ID_RE = /^[A-Za-z0-9_-]{1,128}$/;
/**
 * `scenarioId` として受理する形式（M-95 PR2）。CAMPAIGN_ID_RE と同値だが、
 * **別名で持つのは意図** — 片方の zod 制約が変わったときに他方が黙って追随しないように。
 */
const SCENARIO_ID_RE = /^[A-Za-z0-9_-]{1,128}$/;
/** `surveyId` の最大長（`promoSurveyUpsert` の zod と同じ）。 */
const SURVEY_ID_MAX = 128;
/**
 * `surveyId` を Firestore の doc id としてそのまま使ってよいか。
 *
 * **文字種は制限しない** — `promoSurveyUpsert` は 1〜128 文字の任意文字を受けるので、
 * 既に作られた id を後から弾くと過去の回答が読めなくなる。ここで落とすのは
 * **Firestore が doc id として拒否する形**（`INVALID_ARGUMENT` になるもの）だけ:
 *
 *   - `/` を含む（subcollection path に化ける）
 *   - `.` / `..`（相対パス）
 *   - `__...__`（Firestore の予約形式）
 *
 * なぜ要るか: これらを素通しすると `db.collection('promo_surveys').doc(id)` が
 * `INVALID_ARGUMENT` を投げる。webhook 経路ではこれが `not-found` /
 * `failed-precondition` のどちらでもないため 500 になり、**LINE が再送するたびに
 * 同じ失敗を繰り返す**（`isSafeWebhookEventId` が `line_events` で防いでいるのと同じ故障）。
 * postback の `data` は顧客の端末から任意の値を送れるので、実際に到達しうる。
 */
export function isSafeSurveyDocId(surveyId) {
    if (surveyId.length === 0 || surveyId.length > SURVEY_ID_MAX)
        return false;
    if (surveyId.includes('/'))
        return false;
    if (surveyId === '.' || surveyId === '..')
        return false;
    return !/^__.*__$/.test(surveyId);
}
/**
 * トーク版アンケートの postback `data` を組み立てる（純関数）。
 *
 * `URLSearchParams` を使うので `surveyId` / `campaignId` に区切り文字が混じっても
 * percent-encode されて壊れない。**上限超過は呼出側が `isSurveyPostbackDataWithinLimit`
 * で弾く**（ここで throw すると、配信作成の途中で例外が飛んで原因が見えにくくなる）。
 */
export function buildSurveyPostbackData(args) {
    const params = new URLSearchParams();
    params.set('t', SURVEY_POSTBACK_TYPE);
    params.set('s', args.surveyId);
    params.set('c', args.choice);
    if (args.campaignId)
        params.set('k', args.campaignId);
    if (args.scenarioId)
        params.set('sc', args.scenarioId);
    return params.toString();
}
/** postback `data` が LINE の上限に収まるか。 */
export function isSurveyPostbackDataWithinLimit(data) {
    return data.length <= LINE_POSTBACK_DATA_MAX;
}
/**
 * トーク版アンケートの postback `data` を解釈する（該当しなければ null / 純関数）。
 *
 * **postback の `data` は顧客の端末から任意の値を送れる**前提で扱う（M-95 の制約）。
 * ここで見るのは**形式だけ**で、`surveyId` / `choice` の実在・テナント・回答受付中かは
 * 呼出側（`applySurveyAnswer` の transaction 内 read + `pickAnswersDefinedInSurvey`）が判定する。
 *
 * - `t` が一致しないもの（M1-01 の `m1_01_3d__action=...` など）は null = 従来の
 *   `postback_tap` 経路へ落ちる
 * - `choice`（選択肢 id）は**形式のみ**検証する（M-106 / 閉集合検証は廃止）。
 *   保存側 zod と同値の `isValidSurveyOptionId` を使い、**保存できる id はすべて往復する**
 *   ことを構造的に保証する。トリムするのは、zod の `.trim()` を通って Firestore に入る id が
 *   常にトリム済みだから — トリムしないと前後空白付きの詐称 postback が定義照合で落ち、
 *   「タップしたのに何も記録されない」無言の取りこぼしになる
 * - `campaignId` は形式不正なら**回答ごと落とさずに帰属だけ捨てる** — 帰属は集計の付随情報で、
 *   本体（choice）を巻き添えにする価値がない
 */
export function parseSurveyPostbackData(data) {
    if (!data)
        return null;
    // 上限超過は LINE が送ってこない値 = 詐称。パースする前に落とす。
    if (data.length > LINE_POSTBACK_DATA_MAX)
        return null;
    const params = new URLSearchParams(data);
    if (params.get('t') !== SURVEY_POSTBACK_TYPE)
        return null;
    // doc id として使えない形（予約形式 / パス区切り）はここで落とす。素通しすると
    // Firestore が INVALID_ARGUMENT を投げ、webhook が 500 → 再送ループになる。
    const surveyId = params.get('s')?.trim();
    if (!surveyId || !isSafeSurveyDocId(surveyId))
        return null;
    // 保存側 zod は `.trim()` してから長さを見る（= Firestore の id はトリム済み）。
    // ここも同じ順で扱い、**トリム後の値**を payload に載せる。
    const choice = params.get('c')?.trim();
    if (!isValidSurveyOptionId(choice))
        return null;
    const campaignId = params.get('k')?.trim();
    const scenarioId = params.get('sc')?.trim();
    return {
        surveyId,
        choice,
        ...(campaignId && CAMPAIGN_ID_RE.test(campaignId) ? { campaignId } : {}),
        // `campaignId` と同じ扱い: 形式不正なら回答ごと落とさずに帰属だけ捨てる。
        ...(scenarioId && SCENARIO_ID_RE.test(scenarioId) ? { scenarioId } : {}),
    };
}
/**
 * 「回答を修正する」の postback `data` を組み立てる（純関数 / M-111）。
 *
 * **帰属（`k=` / `sc=`）を載せるのが要点**。載せないと、修正で入れ直した回答が
 * 帰属なしになり、`applySurveyAnswer` が**旧値を削除する**（帰属なしの再回答で
 * 旧値を残すと最新回答が過去の配信由来と誤帰属されるため、そういう設計になっている）。
 * 結果、**修正した瞬間に配信 → 回答の紐付けが消えて CV 計測だけ壊れる**。
 */
export function buildSurveyEditPostbackData(args) {
    const params = new URLSearchParams();
    params.set('t', SURVEY_EDIT_POSTBACK_TYPE);
    params.set('s', args.surveyId);
    if (args.campaignId)
        params.set('k', args.campaignId);
    if (args.scenarioId)
        params.set('sc', args.scenarioId);
    return params.toString();
}
/**
 * 「回答を修正する」の postback `data` を解釈する（該当しなければ null / 純関数 / M-111）。
 *
 * 検証の粒度は `parseSurveyPostbackData` と揃える（形式だけ見て、実在・テナント・
 * 受付中かは呼出側が定義を読んで判定する）。
 */
export function parseSurveyEditPostbackData(data) {
    if (!data)
        return null;
    if (data.length > LINE_POSTBACK_DATA_MAX)
        return null;
    const params = new URLSearchParams(data);
    if (params.get('t') !== SURVEY_EDIT_POSTBACK_TYPE)
        return null;
    const surveyId = params.get('s')?.trim();
    if (!surveyId || !isSafeSurveyDocId(surveyId))
        return null;
    const campaignId = params.get('k')?.trim();
    const scenarioId = params.get('sc')?.trim();
    return {
        surveyId,
        ...(campaignId && CAMPAIGN_ID_RE.test(campaignId) ? { campaignId } : {}),
        ...(scenarioId && SCENARIO_ID_RE.test(scenarioId) ? { scenarioId } : {}),
    };
}
/**
 * 旧 4 択の表示文言を解決する（テナント上書き → 既定文言）。
 *
 * **空文字は既定に倒す**（`??` ではなく `||`）— `promoSurveyUpsert` は値のあるキーだけを
 * 保存する仕様で、空欄は「既定文言を使う」の意味になる。
 *
 * M-106 時点の呼出元は `buildLegacyChoiceOptions`（`promoSurveySchema.ts`）**のみ** =
 * 旧形式 doc を `questions` へ変換するときの文言解決。トーク版の組み立て
 * （`buildSurveyQuickReplyItems`）は `option.label` を直接使うようになったため、
 * **この規則が効くのは「旧 doc → 新形式」の変換 1 箇所だけ**になった
 * （変換後は文言が `label` に焼き込まれ、以降は全経路が同じ値を見る）。
 */
export function resolveSurveyChoiceLabel(choice, choiceLabels) {
    return choiceLabels?.[choice]?.trim() || DEFAULT_SURVEY_CHOICE_LABELS[choice];
}
/**
 * クイックリプライのラベルを LINE の上限（20 文字）に収める（純関数）。
 *
 * 選択肢文言は最大 60 文字まで設定できる（`promoSurveyUpsert` の zod）ため、長い文言を
 * 入れたテナントでは切り詰めが必要。**切り詰めたことが顧客に分かる**よう末尾を `…` にする
 * （無言で切ると「文が途中で終わっている」ようにしか見えない）。
 */
export function truncateQuickReplyLabel(label) {
    const trimmed = label.trim();
    if (trimmed.length <= LINE_QUICK_REPLY_LABEL_MAX)
        return trimmed;
    return `${trimmed.slice(0, LINE_QUICK_REPLY_LABEL_MAX - 1)}…`;
}
/**
 * トーク版アンケートのクイックリプライを組み立てる（純関数 / 表示順は `args.options` の順）。
 *
 * - `label`: 20 文字に切り詰めたボタン文言
 * - `displayText`: **切り詰めない完全な文言**（顧客の発言としてトークに残るため、
 *   記録としては完全な方がよい。LINE の上限は 300 文字で、選択肢の 60 文字上限に収まる）
 * - `data`: `buildSurveyPostbackData` の出力（`c=` は選択肢 id）
 *
 * **件数・長さのガードはここでは掛けない**（`areSurveyQuickReplyItemsWithinCount` /
 * `areSurveyQuickReplyItemsWithinLimit`）。ここで throw / 切り落としをすると、
 * 配信作成の途中で例外が飛ぶか、黙って選択肢が減る。判断は送信直前の 1 箇所に集める。
 */
export function buildSurveyQuickReplyItems(args) {
    return args.options.map((option) => {
        const label = option.label.trim();
        return {
            label: truncateQuickReplyLabel(label),
            data: buildSurveyPostbackData({
                surveyId: args.surveyId,
                choice: option.id,
                ...(args.campaignId ? { campaignId: args.campaignId } : {}),
                ...(args.scenarioId ? { scenarioId: args.scenarioId } : {}),
            }),
            displayText: label,
        };
    });
}
/**
 * 組み立てた項目がすべて LINE の postback `data` 上限に収まるか（送信前の検証用）。
 *
 * `surveyId` + `campaignId` が長いと超えうる。**送信を止める判断は呼出側**が行う
 * （超えたまま送ると、その項目のタップが LINE 側で無効になり無言で回答が取れない）。
 */
export function areSurveyQuickReplyItemsWithinLimit(items) {
    return items.every((item) => isSurveyPostbackDataWithinLimit(item.data));
}
/**
 * 組み立てた項目が LINE のクイックリプライ**件数**上限に収まるか（M-106 / 送信前の検証用）。
 *
 * 定義側の上限（`SURVEY_MAX_OPTIONS` = 20）は**据え置き**（回答ページへのリンク版は
 * 20 択で問題なく動くので、トーク版の都合で定義を縛らない）。したがって
 * 「リンク版なら送れるがトーク版では送れない」定義が正当に存在し、
 * **その差をここで fail-closed に受け止める**のがこの関数の役目。
 *
 * `areSurveyQuickReplyItemsWithinLimit`（1 項目あたりの `data` 長）とは別物で、
 * 送信前には**両方**通す必要がある。
 */
export function areSurveyQuickReplyItemsWithinCount(items) {
    return items.length <= LINE_QUICK_REPLY_MAX_ITEMS;
}
/**
 * 回答直後にトークへ返す確認文言（純関数 / `replyToken` で送る想定 = 追加費用なし）。
 *
 * **`not_interested` だけ文面を分ける** — 「関心がない」と答えた人に「またお得な情報を
 * お届けします」と返すのは、設計書 §6 の「嫌われ防止」（`not_interested` は配信抑制に
 * 寄与する回答）と正面から衝突する。
 *
 * M-106: 引数は**確度キー**であって選択肢 id ではない。任意の選択肢は確度に紐づかない
 * （`PromoSurveyOption.interestChoice` が未設定）ことがあるため `undefined` を受け、
 * その場合は汎用文面を返す。呼出側は
 * `getQuickReplyDeliverableQuestion(survey)?.options.find(o => o.id === payload.choice)
 * ?.interestChoice` を渡すこと — **選択肢 id をそのまま渡してはいけない**
 * （型では防げるが、`as` で潰すと「特にない」に継続案内が返る）。
 */
export function renderInlineSurveyThanksText(choice) {
    if (choice === 'not_interested') {
        return 'ご回答ありがとうございます。\nご案内の頻度を控えめにいたします。';
    }
    return 'ご回答ありがとうございます。\nまたお得な情報をお届けします。';
}
/**
 * 受付を終えたアンケートへの回答に返す文言（純関数）。
 *
 * 配信済みのメッセージは**トーク画面に残り続ける**ため、アンケートを `archived` にした後や
 * 削除した後にボタンを押されうる。無反応にすると「押したのに何も起きない」になるので、
 * 受け付けられなかったことをその場で返す。
 */
export function renderInlineSurveyClosedText() {
    return 'このアンケートは受付を終了しました。\nご回答ありがとうございました。';
}
/**
 * 「回答を修正する」を押されたが選択肢を再提示できないときの文言（純関数 / M-111）。
 *
 * **受付終了（`renderInlineSurveyClosedText`）とは別の文面にする** — 顧客から見て
 * 「もう答えられない」と「今は直せないが回答は残っている」は別の話で、同じ文面にすると
 * 「回答が無かったことになったのか」と受け取られる。
 *
 * 到達経路は LIFF 未設定の環境や、配信後の定義編集で選択肢を再提示できなくなった場合。
 * どちらも**回答自体は記録済み**なので、そこを明示する。
 */
export function renderInlineSurveyEditUnavailableText() {
    return 'ただいま回答の修正を受け付けられません。\n先ほどのご回答は記録されています。';
}
//# sourceMappingURL=promoSurveyQuickReply.js.map